import { useParams, Link } from "react-router-dom";
import { useQuery } from "react-query";
import {
  ArrowLeft,
  GitBranch,
  Star,
  GitFork,
  AlertCircle,
  Archive,
  ExternalLink,
  Calendar,
  Code,
  Globe,
  Lock,
  Building,
  Tags,
  Users,
} from "lucide-react";
import { apiService } from "../services/api";
import { Repository } from "../types";

export default function RepositoryDetailPage() {
  const { owner, name } = useParams<{ owner: string; name: string }>();

  const {
    data: repoData,
    isLoading,
    error,
  } = useQuery(
    ["repository", owner, name],
    () => apiService.getRepository(owner!, name!),
    {
      enabled: !!owner && !!name,
    },
  );

  const repo: Repository | undefined = repoData?.data?.data;

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (error || !repo) {
    return (
      <div className="card p-12 text-center">
        <AlertCircle className="mx-auto h-12 w-12 text-red-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">
          Repository not found
        </h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          The repository you're looking for doesn't exist or you don't have
          access to it.
        </p>
        <div className="mt-6">
          <Link to="/repositories" className="btn btn-primary">
            Back to Repositories
          </Link>
        </div>
      </div>
    );
  }

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const VisibilityIcon =
    repo.visibility === "public"
      ? Globe
      : repo.visibility === "internal"
        ? Building
        : Lock;

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <Link
          to="/repositories"
          className="inline-flex items-center text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 mb-4"
        >
          <ArrowLeft className="mr-1 h-4 w-4" />
          Back to Repositories
        </Link>

        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {repo.fullName}
              </h1>
              {repo.archived && (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200">
                  <Archive className="h-3 w-3" />
                  Archived
                </span>
              )}
              {repo.fork && (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                  <GitFork className="h-3 w-3" />
                  Fork
                </span>
              )}
            </div>
            {repo.description && (
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                {repo.description}
              </p>
            )}
          </div>
          <a
            href={repo.htmlUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-secondary flex items-center gap-2 shrink-0"
          >
            <ExternalLink className="h-4 w-4" />
            View on GitHub
          </a>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Overview Card */}
          <div className="card p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Overview
            </h2>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  Owner
                </dt>
                <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                  {repo.owner}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  Default Branch
                </dt>
                <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100 flex items-center gap-1">
                  <GitBranch className="h-3.5 w-3.5 text-gray-400" />
                  {repo.defaultBranch}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  Visibility
                </dt>
                <dd className="mt-1">
                  <span
                    className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      repo.visibility === "public"
                        ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                        : repo.visibility === "internal"
                          ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200"
                          : "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200"
                    }`}
                  >
                    <VisibilityIcon className="h-3 w-3" />
                    {repo.visibility}
                  </span>
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  Language
                </dt>
                <dd className="mt-1">
                  {repo.language ? (
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                      <Code className="h-3 w-3" />
                      {repo.language}
                    </span>
                  ) : (
                    <span className="text-sm text-gray-400">Not detected</span>
                  )}
                </dd>
              </div>
            </dl>
          </div>

          {/* Dates Card */}
          <div className="card p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Timeline
            </h2>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 flex items-center gap-1">
                  <Calendar className="h-3.5 w-3.5" />
                  Created
                </dt>
                <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                  {formatDate(repo.createdAt)}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 flex items-center gap-1">
                  <Calendar className="h-3.5 w-3.5" />
                  Last Updated
                </dt>
                <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                  {formatDate(repo.updatedAt)}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 flex items-center gap-1">
                  <Calendar className="h-3.5 w-3.5" />
                  Last Pushed
                </dt>
                <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                  {formatDate(repo.pushedAt)}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 flex items-center gap-1">
                  <Calendar className="h-3.5 w-3.5" />
                  Last Commit
                </dt>
                <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                  {(() => {
                    if (!repo.contributors || repo.contributors.length === 0)
                      return "—";
                    const latest = repo.contributors.reduce((a, b) =>
                      new Date(a.commitDate) > new Date(b.commitDate) ? a : b,
                    );
                    return formatDate(latest.commitDate);
                  })()}
                </dd>
              </div>
            </dl>
          </div>

          {/* Statistics & Status Row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {/* Stats Card */}
            <div className="card p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                Statistics
              </h2>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                    <Star className="h-4 w-4" />
                    Stars
                  </span>
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {repo.starsCount}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                    <GitFork className="h-4 w-4" />
                    Forks
                  </span>
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {repo.forksCount}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                    <AlertCircle className="h-4 w-4" />
                    Open Issues
                  </span>
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {repo.openIssues}
                  </span>
                </div>
              </div>
            </div>

            {/* Status Card */}
            <div className="card p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                Status
              </h2>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    Archived
                  </span>
                  <span
                    className={`text-sm font-medium ${
                      repo.archived
                        ? "text-amber-600 dark:text-amber-400"
                        : "text-green-600 dark:text-green-400"
                    }`}
                  >
                    {repo.archived ? "Yes" : "No"}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    Disabled
                  </span>
                  <span
                    className={`text-sm font-medium ${
                      repo.disabled
                        ? "text-red-600 dark:text-red-400"
                        : "text-green-600 dark:text-green-400"
                    }`}
                  >
                    {repo.disabled ? "Yes" : "No"}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    Fork
                  </span>
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {repo.fork ? "Yes" : "No"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Organization Card */}
          {repo.businessArea && (
            <div className="card p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
                <Building className="h-5 w-5" />
                Organization
              </h2>
              <div className="space-y-3">
                {repo.businessAreaName && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      Business Area
                    </span>
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {repo.businessAreaName} ({repo.businessArea})
                    </span>
                  </div>
                )}
                {repo.businessUnitDescription && (
                  <div>
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      Business Unit
                    </span>
                    <p className="mt-0.5 text-sm font-medium text-gray-900 dark:text-gray-100">
                      {repo.businessUnitDescription} ({repo.businessUnit})
                    </p>
                  </div>
                )}
                {repo.productAreaName && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      Product Area
                    </span>
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {repo.productAreaName}
                    </span>
                  </div>
                )}
                {repo.companyName && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      Company
                    </span>
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {repo.companyName}
                    </span>
                  </div>
                )}
                {repo.positionCostCenterId && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      Cost Center
                    </span>
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {repo.positionCostCenterId}
                    </span>
                  </div>
                )}
                {(repo.recentContributorCount ?? 0) > 0 && (
                  <div className="flex items-center justify-between pt-2 border-t border-gray-200 dark:border-gray-700">
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      Based on
                    </span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {repo.recentContributorCount} contributors
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Custom Properties Card */}
          {repo.customProperties && repo.customProperties.length > 0 && (
            <div className="card p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
                <Tags className="h-5 w-5" />
                Custom Properties
              </h2>
              <div className="space-y-3">
                {repo.customProperties.map((prop) => (
                  <div
                    key={prop.propertyName}
                    className="flex items-center justify-between"
                  >
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {prop.propertyName}
                    </span>
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {prop.value || "—"}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recent Contributors Card */}
          {repo.contributors && repo.contributors.length > 0 && (
            <div className="card p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
                <Users className="h-5 w-5" />
                Recent Contributors
              </h2>
              <div className="space-y-3">
                {repo.contributors.map((contrib) => (
                  <div key={contrib.login} className="flex items-center gap-3">
                    <img
                      src={contrib.avatarUrl}
                      alt={contrib.login}
                      className="h-8 w-8 rounded-full"
                    />
                    <div>
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {contrib.login}
                      </span>
                      {contrib.email && (
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {contrib.email}
                        </p>
                      )}
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {formatDate(contrib.commitDate)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
